import { mutate, searchContent } from '@echo/api-client/onearly';
import { type ContentGraph, jsonValueOf, readGraph } from './graph';
import {
  AS_PERSON,
  performAsFor,
  sameScope,
  scopeKey,
  type WriteScope,
  writtenScopeOf,
} from './scope';

/**
 * Library › My favorites: a person's shelf in Core (`SM Shelf`, scripts/seeds/README.md). One private
 * node per person and write scope, `{ schema, kind: 'saved' }`, that INCLUDES each book they saved.
 * Saving adds that edge and unsaving removes it, both through `POST /content/mutate`, so nothing is
 * ever created twice for a double tap. The edge points at the version of the book that was saved;
 * books are matched by lineage, so a later version of the same book is still "saved".
 *
 * The shelf is written as a member of the Stronger Memory group, like results (`scope.ts`): the
 * book is homed at the group's region, so a mutate naming it as the person alone is relayed there
 * and refused (`RELAY_PERSONAL_WRITE`). Core lets only a node's exact author add to it, so a save
 * goes to the shelf written in the current scope; a shelf written in another scope (as the person,
 * before this) still counts as saved and is still where its own edges are removed.
 */
export interface SavedShelf {
  /** The person's saved shelves, with the scope each was written in (undefined: not one echo writes as). */
  shelves: { uuid: string; scope: WriteScope | undefined }[];
  /** Book lineage → every edge that holds it (one, unless two devices made two shelves). */
  saved: Map<string, { shelfUuid: string; toUuid: string }[]>;
}

export const EMPTY_SHELF: SavedShelf = { shelves: [], saved: new Map() };

/** The person's shelves of kind `saved`, authored by them (never one someone else shares). */
export async function loadSavedShelf(shelfType: string, personUuid: string): Promise<SavedShelf> {
  const response = await searchContent({
    rootContentTypeUuids: [shelfType],
    actorPredicates: [{ edgeTypes: ['AUTHORED_BY'], actorUuids: [personUuid] }],
    maxDepth: 1,
    first: 20,
  });
  if (response.status !== 200) {
    throw new Error(`OnEarly: reading Saved failed (${response.status})`);
  }
  return shelfFromGraph(readGraph(response.data));
}

export function shelfFromGraph(graph: ContentGraph): SavedShelf {
  const shelfUuids = graph.roots.filter((uuid) => {
    const node = graph.nodes.get(uuid);
    const value = node ? jsonValueOf(node) : undefined;
    return (value as { kind?: unknown } | undefined)?.kind === 'saved';
  });
  const saved = new Map<string, { shelfUuid: string; toUuid: string }[]>();
  for (const edge of graph.edges) {
    if (edge.edgeType !== 'INCLUDES' || !shelfUuids.includes(edge.fromUuid)) continue;
    const lineage = edge.toLineageUuid ?? edge.toUuid;
    saved.set(lineage, [
      ...(saved.get(lineage) ?? []),
      { shelfUuid: edge.fromUuid, toUuid: edge.toUuid },
    ]);
  }
  const shelves = shelfUuids.map((uuid) => ({ uuid, scope: writtenScopeOf(graph, uuid) }));
  return { shelves, saved };
}

/** The shelf written in `scope`, which a save to that scope is added to; undefined when none is. */
export function shelfForScope(
  shelves: SavedShelf['shelves'],
  scope: WriteScope,
): string | undefined {
  return shelves.find((entry) => sameScope(entry.scope, scope))?.uuid;
}

/** A key for one save or unsave: Core replays a retried request instead of doing it twice. */
function idempotencyKey(): string {
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16));
  hex[12] = '4';
  hex[16] = ((Number.parseInt(hex[16] as string, 16) & 3) | 8).toString(16);
  const h = hex.join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Saves a book (by the uuid of the version shown) to the shelf, written in `scope`, making the
 * shelf when there is none in that scope. Returns the shelf's uuid.
 */
export async function saveToShelf(
  shelfType: string,
  shelfUuid: string | undefined,
  bookUuid: string,
  scope: WriteScope,
): Promise<string> {
  const performAs = performAsFor(scope);
  const edge = { toContentUuid: bookUuid, edgeType: 'INCLUDES' as const, carbonCopy: false };
  const response = await mutate(
    shelfUuid
      ? {
          idempotencyKey: idempotencyKey(),
          ...(performAs ? { performAs } : {}),
          setEdges: [{ fromContentUuid: shelfUuid, ...edge }],
        }
      : {
          idempotencyKey: idempotencyKey(),
          ...(performAs ? { performAs } : {}),
          addNodes: [
            {
              clientId: 'shelf',
              variant: 'INSTANCE',
              contentTypeUuid: shelfType,
              value: { jsonValue: { schema: 1, kind: 'saved' } },
            },
          ],
          setEdges: [{ fromClientId: 'shelf', ...edge }],
        },
  );
  if (response.status !== 200) {
    throw new Error(`OnEarly: saving the book failed (${response.status})`);
  }
  if (shelfUuid) return shelfUuid;
  const made = response.data.data.clientIdMap.find((entry) => entry.clientId === 'shelf')?.uuid;
  if (!made) throw new Error('OnEarly: saving the book returned no shelf');
  return made;
}

/**
 * Takes a book off every shelf that holds it, one mutate per scope: each edge is removed as the
 * scope its shelf was written in, as the person where that is not known.
 */
export async function removeFromShelf(
  edges: readonly { shelfUuid: string; toUuid: string }[],
  shelves: SavedShelf['shelves'],
): Promise<void> {
  const byScope = new Map<string, { scope: WriteScope; edges: typeof edges }>();
  for (const edge of edges) {
    const scope = shelves.find((entry) => entry.uuid === edge.shelfUuid)?.scope ?? AS_PERSON;
    const key = scopeKey(scope);
    byScope.set(key, { scope, edges: [...(byScope.get(key)?.edges ?? []), edge] });
  }
  for (const { scope, edges: scoped } of byScope.values()) {
    const performAs = performAsFor(scope);
    const response = await mutate({
      idempotencyKey: idempotencyKey(),
      ...(performAs ? { performAs } : {}),
      removeEdges: scoped.map(({ shelfUuid, toUuid }) => ({
        fromContentUuid: shelfUuid,
        toContentUuid: toUuid,
        edgeType: 'INCLUDES' as const,
        edgeKey: null,
      })),
    });
    if (response.status !== 200) {
      throw new Error(`OnEarly: removing the book from Saved failed (${response.status})`);
    }
  }
}
